import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useNodePerformanceContext } from '@lyearn/lygo-core';
import { Box } from '@lyearn/ui';
import { useCourse } from '@/modules/corporate/modules/course/courseContext';
import CourseEntity from '@/modules/corporate/modules/course/entity/Course';
import { Maybe } from '@/types/schema';

import { QuizCardContainerProps } from '../QuizCardContainer/types';
import CourseAttachSurvey from './components/CourseAttachSurvey';
import CourseSurveyCard from './components/CourseSurveyCard';
import InlineSurveyContainer from './components/InlineSurveyContainer';
import SurveyEditorModal from './components/SurveyEditorModal';

const fetchDetails = (props: any) => {
  if (props.node) {
    return props.node.attrs;
  }
  return props;
};

function SurveyCardContainer(props: QuizCardContainerProps) {
  const { contentId, updateAttribute, editable, duration, id, inlinePlayer } = fetchDetails(props);
  const { course } = useCourse();
  const { setNodePerformance } = useNodePerformanceContext();
  const [showEditorModal, { on: openEditorModal, off: closeEditorModal }] = useToggle(false);

  const onLoad = React.useCallback(
    (contentDuration?: Maybe<number>) => {
      if (
        editable &&
        contentDuration &&
        duration &&
        Math.trunc(contentDuration) != Math.trunc(duration)
      ) {
        updateAttribute({ duration: contentDuration });
      }
    },
    [duration, editable, updateAttribute],
  );

  const setSurveyPerformance = React.useCallback(() => {
    id && setNodePerformance?.({ id, blockType: 'survey', progress: 100 });
  }, [id, setNodePerformance]);

  React.useEffect(() => {
    // set performance for empty block where survey is still to be attached
    if (!editable && !contentId && !duration) {
      setSurveyPerformance();
    }
  }, [duration, editable, contentId, setSurveyPerformance]);

  if (!CourseEntity.id(course)) {
    return null;
  }

  const renderSurveyCard = () => {
    if (inlinePlayer && !editable) {
      return (
        <InlineSurveyContainer
          courseId={CourseEntity.id(course)}
          languageType={course.languageType}
          surveyId={contentId}
          onLoad={onLoad}
          onSubmit={setSurveyPerformance}
        />
      );
    }
    return (
      <CourseSurveyCard
        courseId={CourseEntity.id(course)}
        languageType={course.languageType}
        surveyId={contentId}
        onLoad={onLoad}
        onSubmit={setSurveyPerformance}
      />
    );
  };

  const renderContentSearch = () => {
    return editable ? (
      <CourseAttachSurvey
        id={CourseEntity.id(course)}
        updateAttribute={updateAttribute}
        onCreateNew={openEditorModal}
      />
    ) : null;
  };

  return (
    <>
      <Box className="mt-24">{contentId ? renderSurveyCard() : renderContentSearch()}</Box>
      {editable ? (
        <SurveyEditorModal
          contentId={contentId}
          open={showEditorModal}
          updateAttribute={updateAttribute}
          onClose={closeEditorModal}
        />
      ) : null}
    </>
  );
}

export default React.memo(SurveyCardContainer);
